import React, { useState } from 'react';

interface ProvideCookiesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (cookies: string) => void;
}

const ProvideCookiesModal: React.FC<ProvideCookiesModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [cookies, setCookies] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (cookies.trim()) {
      onSubmit(cookies);
      setCookies('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg max-w-2xl w-full p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-zinc-200">Provide Cookies</h2>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="cookies" className="block text-sm font-medium text-zinc-300 mb-2">
              Cookie String
            </label>
            <div className="relative">
              <textarea
                id="cookies"
                value={cookies}
                onChange={(e) => setCookies(e.target.value)}
                rows={8}
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/20 transition-all duration-300 text-zinc-100 placeholder-zinc-500 font-mono text-sm resize-none"
                placeholder='Paste your cookies here...&#10;&#10;Example:&#10;sessionId=abc123; userId=789; theme=dark'
                required
              />
              <div className="absolute top-3 right-3">
                <svg className="w-5 h-5 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              </div>
            </div>
            <p className="mt-2 text-xs text-zinc-500">
              Enter cookies in the format: name=value; name2=value2
            </p>
          </div>

          <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-zinc-300">
                <p className="font-medium mb-1">Pro Tip:</p>
                <p className="text-zinc-400">You can copy cookies from your browser's developer tools. Open DevTools → Application → Cookies, then copy the cookie string.</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg font-medium text-zinc-300 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!cookies.trim()}
              className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg font-medium text-white disabled:cursor-not-allowed transition-all duration-200"
            >
              Submit Cookies
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProvideCookiesModal;